import { useContext } from 'react';
import { Bars3Icon, MagnifyingGlassIcon, UserCircleIcon, CreditCardIcon, ShieldCheckIcon, ArrowRightOnRectangleIcon, ChevronDownIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useImpersonation } from '../../context/ImpersonationContext.js';

import { AuthContext } from '../../utils/auth.js';
import { isAdminEmail } from '../../utils/adminUsers.js';
import cn from '../../ui/cn.js';
import Kbd, { modKey } from '../../ui/Kbd.js';
import Avatar from '../../ui/Avatar.js';
import Dropdown from '../../ui/Dropdown.js';
import CreditsPill from '../Credits.js';
import DevDarkModeToggle from '../DevDarkModeToggle.js';
import { signOut } from './Sidebar.js';

export default function Topbar({ title, onOpenSidebar, onOpenPalette, actions }) {
  const { currentUser } = useContext(AuthContext);
  const email = currentUser?.email || '';
  const name = currentUser?.displayName || '';
  const canAccessAdmin = isAdminEmail(email);
  const environment = process.env.REACT_APP_ENV;
  const { canImpersonate, isImpersonating, openPicker } = useImpersonation();

  const items = [
    [
      { label: 'Your profile', to: '/account', icon: UserCircleIcon },
      { label: 'Billing & pages', to: '/billing', icon: CreditCardIcon },
      canAccessAdmin ? { label: 'Admin console', to: '/admin', icon: ShieldCheckIcon } : null,
    ].filter(Boolean),
    [{ label: 'Log out', onClick: signOut, icon: ArrowRightOnRectangleIcon, danger: true }],
  ];

  return (
    <header className="ex-topbar app-topbar sticky top-0 z-40 flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 px-4 sm:gap-4 sm:px-6 lg:px-8">
      <button type="button" className="-ml-1.5 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 lg:hidden" onClick={onOpenSidebar}>
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="flex min-w-0 flex-1 items-center gap-2">
        {title && <h2 className="truncate text-sm font-semibold text-gray-900">{title}</h2>}
        {environment && environment !== 'production' && (
          <span className="hidden rounded-md bg-amber-50 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-inset ring-amber-200 sm:inline-flex">
            {environment}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {actions}
        <button
          type="button"
          onClick={onOpenPalette}
          className="hidden h-8 items-center gap-2 rounded-lg bg-gray-100 px-2.5 text-xs font-medium text-gray-500 transition hover:bg-gray-200/70 hover:text-gray-700 md:inline-flex"
        >
          <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
          <span>Search</span>
          <span className="ml-1 flex items-center gap-0.5">
            <Kbd>{modKey}</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>

        {canImpersonate && (
          <button
            type="button"
            onClick={openPicker}
            title="View the platform as another user"
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium transition',
              isImpersonating ? 'bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-200 hover:bg-amber-200/70' : 'bg-gray-100 text-gray-600 hover:bg-gray-200/70 hover:text-gray-900',
            )}
          >
            <EyeIcon className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">View as</span>
          </button>
        )}

        <CreditsPill />

        <DevDarkModeToggle className="hidden h-8 items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 text-xs font-medium text-gray-600 transition hover:bg-gray-200/70 sm:inline-flex" />

        <Dropdown
          align="right"
          width="w-60"
          header={
            <div className="min-w-0">
              <p className="text-2xs uppercase tracking-wider text-gray-400">Signed in as</p>
              <p className="truncate text-sm font-medium text-gray-900">{email}</p>
            </div>
          }
          items={items}
          button={
            <span className={cn('flex items-center gap-2 rounded-lg p-1 pr-1.5 text-left transition hover:bg-gray-100')}>
              <Avatar name={name} email={email} size="sm" />
              <ChevronDownIcon className="hidden h-4 w-4 text-gray-400 sm:block" aria-hidden="true" />
            </span>
          }
          buttonClassName="rounded-lg focus-visible:ring-2 focus-visible:ring-indigo-500"
          label="Account menu"
        />
      </div>
    </header>
  );
}
